import { supabase } from '@/supabase';
import type { Message } from '~/types';
import { useAuthStore } from '#imports';

const DEFAULT_PAGE_SIZE = Number.MAX_SAFE_INTEGER;
const DEFAULT_SORT_FIELD = 'created_at';

export const useMessageStore = defineStore('messageStore', () => {
    const authStore = useAuthStore();
    const { active_emp, active_emp_id, isDemo, profile } = storeToRefs(authStore);

    const _isPending = ref<boolean>(false);
    const messages = ref<Array<Message>>([]);
    const messages_cnt = ref<number>(0);
    const pendingMsgCnt = ref<number>(0);

    const isPending = computed(() => { return _isPending.value });
    
    const params = ref<MessageParams>({
        isAsc: false,
        orderBy: DEFAULT_SORT_FIELD,
        page: 1,
        perPage: DEFAULT_PAGE_SIZE,
        search: ''
    });

    const startIndex = computed(() => { return (params.value.page - 1) * params.value.perPage; });
    const endIndex = computed(() => { return startIndex.value + params.value.perPage - 1 });

    function $reset(): void {
        messages.value = [];
        messages_cnt.value = 0;
        _isPending.value = false;
        params.value = {
            isAsc: false,
            orderBy: DEFAULT_SORT_FIELD,
            page: 1,
            perPage: DEFAULT_PAGE_SIZE,
            search: ''
        };
    }

    const realtime = supabase
        .channel('message-channel')
        .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: (isDemo.value ? 'message_demo' : 'message') },
            (payload) => {
                console.log('message-channel payload:', payload);
                fetchMessages();
            }
        )
        .subscribe();

    async function countPendingMessages(): Promise<void> {
        console.log('here we go');
        if (!active_emp.value || !active_emp.value.id) {
            pendingMsgCnt.value = 0;
            return;
        }

        console.log('here we go again');

        let query = supabase
            .from(isDemo.value ? 'message_demo' : 'message')
            .select('*')
            .or(`from.eq.${active_emp.value.id},to.cs.{${active_emp.value.id}}`)
            .not('read', 'cs', `{${active_emp.value.id}}`);

        const { data } = await query;

        console.log('here is the date:', data);

        pendingMsgCnt.value = !!data ? data.length : 0;
    }

    async function fetchMessages(fetchAll: boolean = false): Promise<string> {
        console.log(`fetchMessages(fetchAll: ${fetchAll})`);
        await countPendingMessages();

        if (_isPending.value || !profile.value) return 'Error:  Please try again';
        _isPending.value = true;

        let msg = '';

        let query = supabase
            .from(isDemo.value ? 'message_demo' : 'message')
            .select('id, created_at, from, to, read, dismiss, body, parent', { count: 'exact', head: false })
            .or(`from.eq.${active_emp_id.value},to.cs.{${active_emp_id.value}}`)
            .not('dismiss', 'cs', `{${active_emp_id.value}}`);

        console.log(`fetchMessages() - active_emp.value.id:`, active_emp.value.id, active_emp_id.value);
        if (!fetchAll) {
            query = query.range(startIndex.value, endIndex.value);
        }

        if(!active_emp_id.value) {
            _isPending.value = false;
            return 'Error:  Please try again';
        }

        const { data, count, error } = await query;

        if (error) {
            msg = error.message as string;
        }
        else if (data) {
            messages.value = <unknown>data as Array<Message>;
            messages_cnt.value = count || 0;
        }

        _isPending.value = false;
        return msg;
    }

    async function fetchMessage(id: number): Promise<Message> {
        if (!id) return {} as Message;

        const { data, error } = await supabase
            .from(isDemo.value ? 'message_demo' : 'message')
            .select('*', { count: 'exact', head: false })
            .eq('id', id)
            .single();

        if (error) {
            return {} as Message;
        }
        if (data) {
            return <unknown>data as Message;
        }
        return {} as Message;
    }

    async function addUpdateMessage(item: any, isRead: boolean = false, isDismiss: boolean = false): Promise<string> {
        if (_isPending.value) return 'Error:  Please try again';
        _isPending.value = true;

        let msg = '';

        let query = null;

        if (!item.read) item.read = [];
        if (!item.dismiss) item.dismiss = [];

        if (isRead && item.read.indexOf(active_emp_id.value) < 0) {
            item.read.push(active_emp_id.value);
        }
        else if (!isRead && item.read.indexOf(active_emp_id.value) >= 0) {
            const index = item.read.indexOf(active_emp_id.value);
            item.read.splice(index, 1);
        }

        if (isDismiss && item.dismiss.indexOf(active_emp_id.value) < 0) {
            item.dismiss.push(active_emp_id.value);
        }

        if (!item.id || item.id === 0) {
            query = supabase
                .from(isDemo.value ? 'message_demo' : 'message')
                .insert([
                    {
                        created_at: new Date().toISOString(),
                        from: active_emp_id.value,
                        to: item.to,
                        body: item.body,
                        parent: item.parent > 0 ? item.parent : null,
                        read: [],
                        dismiss: []
                    }
                ])
                .select();
        }
        else {
            query = supabase
                .from(isDemo.value ? 'message_demo' : 'message')
                .update([
                    {
                        read: item.read,
                        dismiss: item.dismiss
                    }
                ])
                .eq('id', item.id)
                .select();
        }

        const { error } = await query;

        if (error) {
            msg = error.message as string;
        }

        _isPending.value = false;
        return msg;
    }

    async function deleteMessage(id: number): Promise<string> {
        if (_isPending.value) return 'Error:  Please try again';
        _isPending.value = true;

        let msg = '';

        const { error } = await supabase
            .from(isDemo.value ? 'message_demo' : 'message')
            .delete()
            .eq('id', id);

        if (error) {
            msg = error.message as string;
        }

        _isPending.value = false;
        return msg;
    }

    return {
        messages,
        messages_cnt,
        params,
        pendingMsgCnt,
        isPending,
        addUpdateMessage,
        deleteMessage,
        fetchMessage,
        fetchMessages,
        $reset
    }
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useMessageStore, import.meta.hot))
}

interface MessageParams {
    isAsc: boolean;
    orderBy: string;
    page: number;
    perPage: number;
    search: string;
}